import type { TextDetection } from '../detect/pii/types';

export type RedactionMode =
  | { kind: 'placeholder'; token?: string }
  | { kind: 'block'; char?: string }
  | { kind: 'type-tag' };

export type RedactOptions = {
  mode?: RedactionMode;
};

const DEFAULT_MODE: RedactionMode = { kind: 'placeholder' };

const tokenFor = (d: TextDetection, mode: RedactionMode): string => {
  switch (mode.kind) {
    case 'placeholder':
      return mode.token ?? '[REDACTED]';
    case 'block': {
      const char = mode.char ?? '█';
      return char.repeat(d.text.length);
    }
    case 'type-tag':
      return `[${d.type.toUpperCase()}]`;
  }
};

/**
 * Apply detections to a text string, replacing each detected span with a
 * redaction token. Detections may be unmerged; this function sorts and
 * skips overlaps defensively.
 */
export const redactText = (
  text: string,
  detections: readonly TextDetection[],
  opts: RedactOptions = {},
): string => {
  if (detections.length === 0) return text;
  const mode = opts.mode ?? DEFAULT_MODE;

  const sorted = [...detections].sort((a, b) => a.start - b.start || a.end - b.end);
  let out = '';
  let cursor = 0;
  for (const d of sorted) {
    if (d.start < cursor) continue; // overlap with previous applied detection
    if (d.start < 0 || d.end > text.length || d.end <= d.start) continue;
    out += text.slice(cursor, d.start);
    out += tokenFor(d, mode);
    cursor = d.end;
  }
  out += text.slice(cursor);
  return out;
};
